import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Upload } from "lucide-react";
import { MediaMetadataIndicator } from "../../components/task/MediaMetadataIndicator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TaskResultsViewer = ({ taskId, mediaFiles, externalLinks = [], resultText = "", userRole, taskStatus, showToast, instanceId = "default", onTaskUpdate = () => {} }) => {
 const [loadedFiles, setLoadedFiles] = useState({});
 const [loadingFiles, setLoadingFiles] = useState({});
 
 // Dispute system state
 const [showDisputeModal, setShowDisputeModal] = useState(false);

 // Individual modal states with unique keys per instance
 const [resultImageModal, setResultImageModal] = useState(null);
 const [mediaImageModal, setMediaImageModal] = useState(null);
 const [disputeReason, setDisputeReason] = useState('');
 const [disputeDescription, setDisputeDescription] = useState('');
 const [disputeEvidence, setDisputeEvidence] = useState([]);
 const [creatingDispute, setCreatingDispute] = useState(false);
 
 // Additional state for the new dispute modal
 const [selectedDisputeTask, setSelectedDisputeTask] = useState(null);
 const [disputeForm, setDisputeForm] = useState({
 reason: '',
 description: ''
 });

 // Handle protected external link access
 const handleExternalLinkAccess = async (originalUrl, linkIndex) => {
 if (!taskId || !showToast) {
 console.error('Missing required props:', { taskId, showToast });
 return;
 }
 
 if (userRole === 'customer' && taskStatus !== 'confirmed') {
 showToast('Доступ к внешним ссылкам предоставляется только после подтверждения задания', 'error');
 return;
 }

 try {
 // Get API base URL
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 if (!API) {
 console.error('Backend URL not configured');
 showToast('Ошибка конфигурации', 'error');
 return;
 }
 
 // Get protected link access through API
 const response = await axios.get(`${API}/api/tasks/${taskId}/external-links/${linkIndex}`);
 if (response.data.access_granted) {
 // Open link in new tab
 window.open(response.data.url, '_blank', 'noopener,noreferrer');
 }
 } catch (error) {
 console.error('Error accessing external link:', error);
 if (error.response?.status === 402) {
 showToast('Доступ к ссылкам предоставляется только после подтверждения задания', 'error');
 } else {
 showToast('Ошибка доступа к ссылке', 'error');
 }
 }
 };

 const loadFile = async (fileIndex) => {
 if (loadedFiles[fileIndex] || loadingFiles[fileIndex]) return;

 setLoadingFiles(prev => ({ ...prev, [fileIndex]: true }));

 try {
 const response = await axios.get(`${API}/tasks/${taskId}/files/${fileIndex}`);
 setLoadedFiles(prev => ({ ...prev, [fileIndex]: response.data }));
 } catch (error) {
 console.error(`Error loading file ${fileIndex}:`, error);
 
 // Определяем тип ошибки более точно
 let errorMessage = "Ошибка загрузки файла";
 let errorType = "general";
 
 if (error.response) {
 // Ошибка с ответом сервера 
 if (error.response.status === 413 || error.response.status === 400) {
 errorMessage = "Файл слишком большой";
 errorType = "size";
 } else if (error.response.status === 404) {
 errorMessage = "Файл не найден";
 errorType = "not_found";
 } else if (error.response.status >= 500) {
 errorMessage = "Ошибка сервера";
 errorType = "server";
 }
 } else if (error.request) {
 // Сетевая ошибка
 errorMessage = "Ошибка сети";
 errorType = "network";
 }
 
 setLoadedFiles(prev => ({ 
 ...prev, 
 [fileIndex]: { 
 error: true, 
 errorMessage, 
 errorType,
 originalError: error
 } 
 }));
 } finally {
 setLoadingFiles(prev => ({ ...prev, [fileIndex]: false }));
 }
 };

 // Download function for files
 const downloadFile = (fileData, fileName, index) => {
 try {
 const link = document.createElement('a');
 link.href = fileData;
 link.download = fileName || `результат_${index + 1}.jpg`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 } catch (error) {
 console.error('Download error:', error);
 // Fallback: open in new tab for manual save
 window.open(fileData, '_blank');
 }
 };

 // Download all files as ZIP
 const downloadAllAsZip = async () => {
 try {
 const zip = new JSZip();
 let filesAdded = 0;

 for (let index = 0; index < mediaFiles.length; index++) {
 try {
 let fileContent;
 let contentType;

 // Use cached file if already loaded
 const loadedFile = loadedFiles[index];
 if (loadedFile?.content) {
 fileContent = loadedFile.content;
 contentType = loadedFile.content_type || 'image/jpeg';
 } else {
 const response = await axios.get(`${API}/tasks/${taskId}/files/${index}`);
 fileContent = response.data.content;
 contentType = response.data.content_type || 'image/jpeg';
 }

 if (fileContent) {
 const extension = (contentType.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
 // content from API is raw base64 (no data: prefix)
 const byteCharacters = atob(fileContent);
 const byteArray = new Uint8Array(byteCharacters.length);
 for (let i = 0; i < byteCharacters.length; i++) {
 byteArray[i] = byteCharacters.charCodeAt(i);
 }
 zip.file(`результат_${index + 1}.${extension}`, byteArray);
 filesAdded++;
 }
 } catch (error) {
 console.error(`Ошибка загрузки файла ${index + 1}:`, error);
 }
 }

 if (filesAdded === 0) {
 alert('Не удалось добавить файлы в архив. Попробуйте скачать файлы по отдельности.');
 return;
 }

 const content = await zip.generateAsync({ type: 'blob' });
 saveAs(content, `задание_${taskId}_файлы.zip`);

 } catch (error) {
 console.error('Ошибка при создании ZIP архива:', error);
 alert('Произошла ошибка при загрузке файлов. Попробуйте скачать файлы по отдельности.');
 }
 };

 // Dispute functions
 const handleEvidenceUpload = (event) => {
 const files = Array.from(event.target.files);
 files.forEach(file => {
 const reader = new FileReader();
 reader.onload = (e) => {
 const base64Data = e.target.result;
 setDisputeEvidence(prev => [...prev, {
 data: base64Data,
 name: file.name,
 type: file.type
 }]);
 };
 reader.readAsDataURL(file);
 });
 };

 const removeEvidence = (index) => {
 setDisputeEvidence(prev =>prev.filter((_, i) =>i !== index));
 };

 const createDispute = async () => {
 if (!disputeReason || !disputeDescription.trim()) {
 alert('Пожалуйста, выберите причину и опишите проблему');
 return;
 }

 setCreatingDispute(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/dispute`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({
 reason: disputeReason,
 description: disputeDescription,
 evidence_files: disputeEvidence.map(e =>e.data)
 })
 });

 const data = await response.json();
 
 if (response.ok) {
 showToast("Спор открыт и передан администратору на рассмотрение", "success");
 setShowDisputeModal(false);
 // Reset form
 setDisputeReason('');
 setDisputeDescription('');
 setDisputeEvidence([]);
 } else {
 showToast(`Ошибка: ${data.detail || 'Не удалось создать спор'}`, "error");
 }
 } catch (error) {
 console.error('Error creating dispute:', error);
 alert('Ошибка сети при создании спора');
 } finally {
 setCreatingDispute(false);
 }
 };

 // Functions for the new dispute modal
 const closeDisputeModal = () => {
 setShowDisputeModal(false);
 setSelectedDisputeTask(null);
 setDisputeForm({ reason: '', description: '' });
 };

 const submitDispute = async () => {
 if (!disputeForm.reason.trim() || !disputeForm.description.trim()) {
 showToast("Заполните все поля жалобы", "error");
 return;
 }
 if (!selectedDisputeTask?.id) {
 showToast("Задание не выбрано", "error");
 return;
 }

 try {
 const API = process.env.REACT_APP_BACKEND_URL;
 const response = await axios.post(`${API}/api/tasks/${selectedDisputeTask.id}/dispute`, {
 reason: disputeForm.reason,
 description: disputeForm.description,
 evidence_files: []
 }, {
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
 });

 showToast("Жалоба подана. Администрация рассмотрит в течение 3 рабочих дней.", "success");
 closeDisputeModal();
 onTaskUpdate();
 } catch (error) {
 const msg = error.response?.data?.detail || "Ошибка при подаче жалобы";
 showToast(msg, "error");
 }
 };

 // Modal component for image viewing with unique z-index per instance
 const ImageModal = ({ src, onClose, isProtectedPreview = false, showToast }) => {
 const zVal = 9000;

 // Блокируем скролл страницы когда модал открыт
 React.useEffect(() => {
 document.body.style.overflow = 'hidden';
 const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
 window.addEventListener('keydown', handleEsc);
 return () => {
 document.body.style.overflow = '';
 window.removeEventListener('keydown', handleEsc);
 };
 }, [onClose]);

 return ReactDOM.createPortal(
 <div
 style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: zVal, display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.92)' }}
 onClick={onClose}
 >
 {/* Заголовок — закреплён вверху */}
 <div
 style={{ flexShrink: 0, background: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}
 onClick={(e) =>e.stopPropagation()}
 >
 <span style={{ color: '#f9fafb', fontSize: 15, fontWeight: 600 }}>
 {isProtectedPreview ? 'Предварительный просмотр' : 'Результат работы'}
 </span>
 <button
 onClick={onClose}
 style={{ color: '#9ca3af', background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: '4px 8px' }}
 >
 ×
 </button>
 </div>

 {/* Область изображения — растягивается, центрирует. Клик мимо фото закрывает */}
 <div
 style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
 onClick={onClose}
 >
 <img
 src={src}
 alt={isProtectedPreview ? 'Предварительный просмотр' : 'Результат работы'}
 style={{
 maxWidth: '100%',
 maxHeight: '100%',
 objectFit: 'contain',
 borderRadius: 6,
 boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
 ...(isProtectedPreview ? { pointerEvents: 'none', userSelect: 'none' } : {})
 }}
 draggable={false}
 onClick={(e) =>e.stopPropagation()}
 onContextMenu={isProtectedPreview ? (e) =>e.preventDefault() : undefined}
 />
 </div>

 {/* Предупреждение для защищённого просмотра */}
 {isProtectedPreview && (
 <div
 style={{ flexShrink: 0, background: '#fff7ed', borderTop: '1px solid #fed7aa', padding: '8px 16px', textAlign: 'center' }}
 onClick={(e) =>e.stopPropagation()}
 >
 <p style={{ fontSize: 12, color: '#c2410c', fontWeight: 600 }}>ПРЕДВАРИТЕЛЬНЫЙ ПРОСМОТР</p>
 <p style={{ fontSize: 11, color: '#9a3412', marginTop: 2 }}>Полное качество будет доступно после подтверждения выполнения задания
 </p>
 </div>
 )}

 {/* Кнопки — закреплены внизу */}
 <div
 style={{ flexShrink: 0, display: 'flex', gap: 12, padding: '12px 16px', background: '#111827' }}
 onClick={(e) =>e.stopPropagation()}
 >
 <button
 onClick={onClose}
 style={{ flex: 1, background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
 >Закрыть
 </button>
 {isProtectedPreview ? (
 <button
 onClick={() =>showToast && showToast("Скачивание доступно после подтверждения выполнения задания", "info")}
 style={{ flex: 1, background: '#d1d5db', color: '#6b7280', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 14, cursor: 'not-allowed' }}
 >Скачивание недоступно
 </button>
 ) : (
 <button
 onClick={() =>downloadFile(src, 'результат.jpg')}
 style={{ flex: 1, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
 >Скачать
 </button>
 )}
 </div>
 </div>,
 document.body
 );
 };

 const renderFile = (file, index) => {
 // For legacy base64 strings, display directly
 if (typeof file === 'string') {
 const isImage = file.startsWith('data:image/');
 const isVideo = file.startsWith('data:video/');

 return (
 <div key={index} className="relative group">
 {isImage ? (
 <div className="relative">
 <img
 src={file}
 alt={`Результат ${index + 1}`}
 className="w-full h-48 sm:h-56 object-cover rounded-md cursor-pointer hover:opacity-75"
 onClick={() =>setResultImageModal({
 src: file,
 isProtectedPreview: false
 })}
 />
 {/* Always visible buttons on mobile, hover on desktop */}
 <div className="absolute inset-0 bg-black bg-opacity-50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
 <div className="flex space-x-2">
 <button
 onClick={(e) => {
 e.stopPropagation();
 setResultImageModal({
 src: file,
 isProtectedPreview: false
 });
 }}
 className="bg-white text-black px-3 py-1 rounded text-sm hover:bg-gray-100"
 >Просмотр
 </button>
 <button
 onClick={(e) => {
 e.stopPropagation();
 downloadFile(file, `результат_${index + 1}.jpg`, index);
 }}
 className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
 >Скачать
 </button>
 </div>
 </div>
 </div>
 ) : isVideo ? (
 <div className="relative">
 <video
 src={file}
 className="w-full h-48 sm:h-56 object-cover rounded-md"
 controls
 poster=""
 />
 <button
 onClick={() =>downloadFile(file, `видео_${index + 1}.mp4`, index)}
 className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
 >
 
 </button>
 </div>
 ) : (
 <div className="w-full h-32 bg-gray-100 rounded-md flex flex-col items-center justify-center">
 <span className="text-gray-500 mb-2">Файл #{index + 1}</span>
 <button
 onClick={() =>downloadFile(file, `файл_${index + 1}`, index)}
 className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
 >Скачать
 </button>
 </div>
 )}
 </div>
 );
 }

 // For new format files, load through API
 const loadedFile = loadedFiles[index];
 const isLoading = loadingFiles[index];

 if (!loadedFile && !isLoading) {
 loadFile(index);
 }

 if (isLoading) {
 return (
 <div key={index} className="w-full h-32 bg-gray-100 rounded-md flex items-center justify-center">
 <div className="flex flex-col items-center gap-2">
 <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
 <span className="text-sm text-gray-500">Загрузка...</span>
 </div>
 </div>
 );
 }

 if (loadedFile?.error) {
 const errorMessage = loadedFile.errorMessage || "Ошибка загрузки файла";
 const errorType = loadedFile.errorType || "general";
 
 // Определяем цвет и иконку в зависимости от типа ошибки
 let errorColor = "red";
 let errorIcon = "";
 
 if (errorType === "size") {
 errorColor = "orange";
 errorIcon = "";
 } else if (errorType === "not_found") {
 errorColor = "yellow";
 errorIcon = "";
 } else if (errorType === "server") {
 errorColor = "red";
 errorIcon = "";
 } else if (errorType === "network") {
 errorColor = "purple";
 errorIcon = "";
 }
 
 return (
 <div key={index} className={`w-full h-32 bg-${errorColor}-50 rounded-md flex flex-col items-center justify-center p-2`}>
 <div className="text-center">
 <span className={`text-sm text-${errorColor}-500`}>{errorIcon} {errorMessage}</span>
 <div className="text-xs text-gray-500 mt-1">Файл #{index + 1}
 </div>
 {/* Try to download using backend API endpoint */}
 <button
 onClick={async () => {
 try {
 const response = await fetch(`${API}/tasks/${taskId}/files/${index}/download`);
 if (response.ok) {
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = `файл_${index + 1}`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } else {
 alert('Не удалось скачать файл');
 }
 } catch (error) {
 console.error('Download error:', error);
 alert('Ошибка при скачивании файла');
 }
 }}
 className="mt-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
 >Попробовать скачать
 </button>
 </div>
 </div>
 );
 }

 if (loadedFile?.data || loadedFile?.content || loadedFile?.content_type) {
 // Handle both old and new API response formats
 const contentType = loadedFile.content_type || loadedFile.type || 'image/jpeg';
 const isAudio = loadedFile.is_audio || contentType.startsWith('audio/');
 const isDocument = loadedFile.is_document || [
 'application/pdf',
 'application/msword',
 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
 'application/vnd.ms-excel',
 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
 'text/plain',
 'text/csv',
 ].includes(contentType);
 const isImage = (contentType.startsWith('image/') || 
 loadedFile.data?.startsWith('data:image/')) && !loadedFile.is_video && !isDocument && !isAudio;
 const isVideo = (contentType.startsWith('video/') || 
 loadedFile.data?.startsWith('data:video/') || 
 loadedFile.is_video === true) && !isDocument && !isAudio;
 const fileSize = loadedFile.file_size ? `${(loadedFile.file_size / 1024 / 1024).toFixed(1)}МБ` : '';
 const fileType = loadedFile.type === 'gridfs' ? '' : '';
 const isProtectedPreview = loadedFile.is_protected_preview;

 // Construct media data URL - handle both old and new formats
 const mediaDataUrl = loadedFile.content 
 ? `data:${contentType};base64,${loadedFile.content}`
 : loadedFile.data;

 return (
 <div key={index} className="relative group">
 {isImage ? (
 <>
 <div className="relative">
 <img
 src={mediaDataUrl}
 alt={`Результат ${index + 1}`}
 className={`w-full h-48 sm:h-56 object-cover rounded-md cursor-pointer ${
 isProtectedPreview ? 'hover:opacity-90' : 'hover:opacity-75'
 }`}
 onClick={() =>setMediaImageModal({
 src: mediaDataUrl,
 isProtectedPreview: isProtectedPreview
 })}
 style={{
 userSelect: isProtectedPreview ? 'none' : 'auto',
 pointerEvents: isProtectedPreview ? 'none' : 'auto',
 contextMenu: isProtectedPreview ? 'none' : 'auto'
 }}
 onContextMenu={isProtectedPreview ? (e) =>e.preventDefault() : undefined}
 onDragStart={isProtectedPreview ? (e) =>e.preventDefault() : undefined}
 />
 <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
 {fileType} {fileSize}
 {isProtectedPreview && <span className="ml-1">PREVIEW</span>}
 </div>
 
 {/* Protection overlay for preview */}
 {isProtectedPreview && (
 <div className="absolute top-2 left-2 bg-orange-500 bg-opacity-90 text-white text-xs px-2 py-1 rounded">ПРЕДВАРИТЕЛЬНЫЙ ПРОСМОТР
 </div>
 )}
 
 <div className="absolute inset-0 bg-black bg-opacity-50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
 <div className="flex space-x-3">
 <button
 onClick={(e) => {
 e.stopPropagation();
 setMediaImageModal({
 src: mediaDataUrl,
 isProtectedPreview: isProtectedPreview
 });
 }}
 className="bg-white text-black px-4 py-2 rounded text-sm hover:bg-gray-100"
 >Просмотр
 </button>
 <button
 onClick={(e) => {
 e.stopPropagation();
 if (isProtectedPreview) {
 showToast("Полное качество доступно после подтверждения задания", "info");
 return;
 }
 downloadFile(mediaDataUrl, `результат_${index + 1}.jpg`, index);
 }}
 className={`px-4 py-2 rounded text-sm ${
 isProtectedPreview 
 ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
 : 'bg-blue-600 text-white hover:bg-blue-700'
 }`}
 >
 {isProtectedPreview ? 'Недоступно' : 'Скачать'}
 </button>
 </div>
 </div>
 
 {/* Preview notice */}
 {isProtectedPreview && (
 <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black to-transparent text-white text-xs p-2 rounded-b-md">
 <p className="text-center">Полное качество после подтверждения задания
 </p>
 </div>
 )}
 </div>
 
 {/* EXIF Metadata indicator - positioned outside image container for visibility */}
 <MediaMetadataIndicator 
 taskId={taskId}
 fileIndex={index}
 isCustomer={userRole === 'customer'}
 showToast={showToast}
 />
 </>
 ) : isVideo ? (
 <div className="relative">
 {(loadedFile.preview_unavailable || (isProtectedPreview && !loadedFile.content)) ? (
 <div className="w-full h-48 sm:h-56 bg-gray-900 rounded-md flex flex-col items-center justify-center text-white p-4 text-center">
 <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-2 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
 </svg>
 <p className="text-sm font-semibold">Превью видео временно недоступно</p>
 <p className="text-xs mt-1 text-gray-300">Полное видео откроется после оплаты</p>
 </div>
 ) : (
 <video
 src={mediaDataUrl}
 className="w-full h-48 sm:h-56 object-cover rounded-md"
 controls
 poster=""
 />
 )}
 <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">VIDEO {fileSize}
 {isProtectedPreview && <span className="ml-1">— превью 10 сек</span>}
 </div>
 
 {isProtectedPreview && !loadedFile.preview_unavailable && loadedFile.content && (
 <div className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none">
 <div className="bg-black bg-opacity-60 text-white px-4 py-2 rounded text-center">
 <p className="font-bold text-sm">ПРЕВЬЮ ВИДЕО</p>
 <p className="text-xs mt-1">Первые 10 секунд</p>
 <p className="text-xs">Полное видео после оплаты</p>
 </div>
 </div>
 )}
 
 {isProtectedPreview ? (
 <div className="absolute bottom-2 left-2 bg-orange-500 text-white px-2 py-1 rounded text-xs">Превью 10 секунд
 </div>
 ) : (
 <button
 onClick={() =>downloadFile(mediaDataUrl, `видео_${index + 1}.mp4`, index)}
 className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
 >Скачать
 </button>
 )}
 </div>
 ) : isAudio ? (
 <div className="w-full bg-gray-900 rounded-md p-3">
 <div className="flex items-center gap-2 mb-2">
 <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
 </svg>
 <span className="text-xs text-gray-300 truncate">{loadedFile.filename || `Аудио ${index + 1}`}</span>
 {fileSize && <span className="text-xs text-gray-500 flex-shrink-0">{fileSize}</span>}
 </div>
 {isProtectedPreview ? (
 <div className="flex flex-col items-center justify-center py-4 gap-2">
 <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
 </svg>
 <span className="text-xs text-orange-300 text-center">Аудио доступно после подтверждения задания</span>
 </div>
 ) : (
 <>
 <audio
 src={mediaDataUrl}
 controls
 className="w-full"
 style={{ filter: 'invert(1) hue-rotate(180deg)' }}
 />
 <button
 onClick={() => {
 const ext = contentType.includes('wav') ? 'wav' : contentType.includes('ogg') ? 'ogg' : contentType.includes('flac') ? 'flac' : contentType.includes('aac') ? 'aac' : 'mp3';
 downloadFile(mediaDataUrl, loadedFile.filename || `аудио_${index + 1}.${ext}`, index);
 }}
 className="mt-2 w-full bg-green-700 text-white py-1 rounded text-xs hover:bg-green-800"
 >Скачать аудио
 </button>
 </>
 )}
 </div>
 ) : isDocument ? (
 <div className="w-full bg-white border border-gray-200 rounded-md p-3">
 <div className="flex items-center gap-2 mb-2">
 <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
 </svg>
 <div className="min-w-0">
 <div className="text-sm font-medium text-gray-800 truncate">{loadedFile.filename || `Документ ${index + 1}`}</div>
 <div className="text-xs text-gray-500">{contentType.split('/').pop().toUpperCase()}{fileSize && ` · ${fileSize}`}</div>
 </div>
 </div>
 {isProtectedPreview ? (
 <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded">
 <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
 </svg>
 <span className="text-xs text-orange-700">Документ доступен после подтверждения задания</span>
 </div>
 ) : (
 <button
 onClick={() =>downloadFile(mediaDataUrl, loadedFile.filename || `документ_${index + 1}`, index)}
 className="w-full mt-1 bg-blue-600 text-white py-1.5 rounded text-xs hover:bg-blue-700 flex items-center justify-center gap-1"
 >
 <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
 </svg>Скачать
 </button>
 )}
 </div>
 ) : (
 <div className="w-full h-32 bg-blue-50 rounded-md flex flex-col items-center justify-center p-2">
 <div className="text-2xl mb-1">FILE</div>
 <span className="text-xs text-gray-600 text-center">Файл #{index + 1}<br/>{fileSize}
 {isProtectedPreview && <><br/>ПРЕВЬЮ</>}
 </span>
 <button
 onClick={() => {
 if (isProtectedPreview) {
 showToast("Полный файл доступен после подтверждения задания", "info");
 return;
 }
 downloadFile(mediaDataUrl, `файл_${index + 1}`, index);
 }}
 className={`mt-1 px-3 py-1 rounded text-xs ${
 isProtectedPreview 
 ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
 : 'bg-blue-600 text-white hover:bg-blue-700'
 }`}
 >
 {isProtectedPreview ? 'Недоступно' : 'Скачать'}
 </button>
 </div>
 )}
 </div>
 );
 }

 return (
 <div key={index} className="w-full h-32 bg-gray-100 rounded-md flex items-center justify-center">
 <span className="text-gray-500">Файл #{index + 1}</span>
 </div>
 );
 };

 return (
 <div className="w-full">
 {resultText && resultText.trim() && (
 <div data-testid="result-text-display" className="mb-4 p-4 rounded-xl border" style={{background:'var(--rv-paper, #F6F6F2)', borderColor:'var(--rv-border, #E2E8F0)'}}>
 <h4 className="text-sm font-semibold mb-2" style={{color:'var(--rv-navy, #0F172A)'}}>Отчёт исполнителя</h4>
 <p className="text-sm whitespace-pre-wrap" style={{color:'var(--rv-text-secondary, #475569)'}}>{resultText}</p>
 </div>
 )}
 {mediaFiles && mediaFiles.length > 0 && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 {mediaFiles.map((file, index) =>renderFile(file, index))}
 </div>
 )}
 
 {/* Add "Download All" button */}
 {mediaFiles && mediaFiles.length > 1 && (
 <div className="mt-4 text-center">
 <button
 onClick={downloadAllAsZip}
 className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
 >Скачать все файлы
 </button>
 </div>
 )}

 {/* ВНЕШНИЕ ССЫЛКИ */}
 {externalLinks && externalLinks.length > 0 && (
 <div className="mt-4">
 <h4 className="text-lg font-semibold mb-3 text-orange-700">Ссылки на большие файлы:</h4>
 <div className="space-y-2">
 {externalLinks.map((link, index) => (
 <div key={index} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div className="flex-grow min-w-0">
 <div className="font-medium text-sm text-orange-800">{link.title || 'Без названия'}</div>
 <div className="text-xs text-gray-600 truncate">
 {(userRole === 'customer' && taskStatus !== 'confirmed') 
 ? ' Ссылка скрыта до подтверждения оплаты' 
 : (link.url || 'URL не указан')}
 </div>
 {link.added_at && (
 <div className="text-xs text-gray-500 mt-1">Добавлено: {new Date(link.added_at).toLocaleString('ru-RU')}
 </div>
 )}
 </div>
 <button
 onClick={() =>handleExternalLinkAccess(link.url, index)}
 className="bg-orange-600 text-white px-3 py-2 rounded-md hover:bg-orange-700 text-sm flex-shrink-0 text-center disabled:bg-gray-400 disabled:cursor-not-allowed"
 disabled={userRole === 'customer' && taskStatus !== 'confirmed'}
 >
 {userRole === 'customer' && taskStatus !== 'confirmed' ? ' Оплатите для доступа' : ' Открыть'}
 </button>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Dispute Button - Only show for customers on completed (not yet confirmed) tasks */}
 {userRole === 'customer' && taskStatus === 'completed' && (
 <div className="mt-4 pt-4 border-t border-gray-200">
 <button
 onClick={() =>setShowDisputeModal(true)}
 className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center gap-2"
 >Открыть спор
 </button>
 <p className="text-xs text-gray-500 mt-1">Если результат не соответствует договоренностям
 </p>
 </div>
 )}

 {/* Dispute Modal */}
 {showDisputeModal && ReactDOM.createPortal(
 <div data-testid="results-dispute-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{zIndex:10050}}>
 <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
 <div className="p-6">
 <div className="flex justify-between items-center mb-4">
 <h3 className="text-xl font-semibold text-gray-900">Открыть спор</h3>
 <button
 onClick={() =>setShowDisputeModal(false)}
 className="text-gray-400 hover:text-gray-600"
 >
 
 </button>
 </div>

 <div className="space-y-4">
 {/* Dispute Reason */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Причина спора *
 </label>
 <select
 value={disputeReason}
 onChange={(e) =>setDisputeReason(e.target.value)}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
 >
 <option value="">Выберите причину...</option>
 <option value="quality_issues">Низкое качество материалов</option>
 <option value="missing_files">Не хватает файлов</option>
 <option value="wrong_content">Неправильное содержание</option>
 <option value="technical_issues">Технические проблемы</option>
 <option value="deadline_missed">Нарушен срок</option>
 <option value="other">Другое</option>
 </select>
 </div>

 {/* Dispute Description */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Подробное описание проблемы *
 </label>
 <textarea
 value={disputeDescription}
 onChange={(e) =>setDisputeDescription(e.target.value)}
 placeholder="Опишите детально, что не соответствует договоренностям..."
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 h-32 resize-none"
 />
 </div>

 {/* Evidence Upload */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Доказательства (скриншоты, фото)
 </label>
 <input
 type="file"
 multiple
 accept="image/*"
 onChange={handleEvidenceUpload}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
 />
 <p className="text-xs text-gray-500 mt-1">Загрузите доказательства вашей правоты (необязательно)
 </p>
 </div>

 {/* Evidence Preview */}
 {disputeEvidence.length > 0 && (
 <div>
 <h4 className="text-sm font-medium text-gray-700 mb-2">Загруженные доказательства:</h4>
 <div className="grid grid-cols-2 gap-2">
 {disputeEvidence.map((evidence, index) => (
 <div key={index} className="relative">
 <img 
 src={evidence.data} 
 alt={evidence.name}
 className="w-full h-40 object-cover rounded border"
 />
 <button
 onClick={() =>removeEvidence(index)}
 className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
 >
 ×
 </button>
 <p className="text-xs text-gray-600 mt-1 truncate">{evidence.name}</p>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* Modal Actions */}
 <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
 <button
 onClick={() =>setShowDisputeModal(false)}
 className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
 >Отмена
 </button>
 <button
 onClick={createDispute}
 disabled={creatingDispute || !disputeReason || !disputeDescription.trim()}
 className="reversia-btn reversia-btn-red reversia-btn-md flex-1"
 >
 {creatingDispute ? 'Создание...' : 'Создать спор'}
 </button>
 </div>
 </div>
 </div>
 </div>
 , document.body)}

 {/* Local Modal for Result Images */}
 {resultImageModal && (
 <ImageModal 
 src={resultImageModal.src} 
 onClose={() =>setResultImageModal(null)} 
 isProtectedPreview={resultImageModal.isProtectedPreview || false}
 showToast={showToast}
 />
 )}

 {/* Local Modal for Media Files */}
 {mediaImageModal && (
 <ImageModal 
 src={mediaImageModal.src} 
 onClose={() =>setMediaImageModal(null)} 
 isProtectedPreview={mediaImageModal.isProtectedPreview || false}
 showToast={showToast}
 />
 )}
 </div>
 );
};
